import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ModerationActionParams {
  action: 'issue_warning' | 'remove_content' | 'suspend_user' | 'remove_and_warn';
  reportId: string;
  userId: string;
  contentType?: string;
  contentId?: string;
  reason: string;
  details?: string;
  suspensionType?: 'temporary' | 'permanent';
  durationDays?: number;
}

export interface UserModerationHistory {
  strikes: number;
  suspensions: {
    id: string;
    suspension_type: string;
    status: string;
    starts_at: string;
    expires_at: string | null;
    reason: string;
  }[];
  recentActions: {
    action_type: string;
    reason: string;
    created_at: string;
    moderator: {
      username: string;
      name: string;
    };
  }[];
}

export function useModerationActions() {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const executeModerationAction = async (params: ModerationActionParams) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('moderate-action', {
        body: params
      });

      if (error) throw error;

      if (data?.result?.action === 'auto_suspended') {
        toast({
          title: '⚠️ Auto-Suspension Triggered',
          description: data.result.message,
          variant: 'default',
        });
      } else {
        toast({
          title: 'Action Completed',
          description: data?.result?.message || 'Moderation action executed successfully',
        });
      }

      return { success: true, data };
    } catch (error) {
      console.error('Moderation action error:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to execute moderation action',
        variant: 'destructive',
      });
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  const getUserModerationHistory = async (userId: string): Promise<UserModerationHistory | null> => {
    try {
      // Get strike count
      const { data: strikeCount } = await supabase
        .rpc('get_user_strike_count', { check_user_id: userId });

      // Get active suspensions
      const { data: suspensions } = await supabase
        .from('user_suspensions')
        .select('id, suspension_type, status, starts_at, expires_at, reason')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      // Get recent moderation actions
      const { data: actions } = await supabase
        .from('user_moderation_actions')
        .select(`
          action_type,
          reason,
          created_at,
          moderator:profiles!moderator_id(username, name)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(10);

      return {
        strikes: strikeCount || 0,
        suspensions: suspensions || [],
        recentActions: actions?.map(a => ({
          action_type: a.action_type,
          reason: a.reason,
          created_at: a.created_at,
          moderator: a.moderator as any
        })) || []
      };
    } catch (error) {
      console.error('Error fetching moderation history:', error);
      return null;
    }
  };

  const checkUserSuspension = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .rpc('is_user_suspended', { check_user_id: userId });
      
      if (error) throw error;
      return data || false;
    } catch (error) {
      console.error('Error checking suspension:', error);
      return false;
    }
  };

  return {
    executeModerationAction,
    getUserModerationHistory,
    checkUserSuspension,
    loading
  };
}